/**
 * Currency utility functions for handling Malaysian Ringgit (RM)
 * Backend stores values in cents, frontend displays in ringgit
 */

/**
 * Format cents value as currency string
 * @param cents - Value in cents (integer)
 * @param currencyPrefix - Optional currency prefix, defaults to 'RM'
 * @returns Formatted currency string (e.g., "RM 12.50")
 */
export const formatCurrency = (cents: number, currencyPrefix = 'RM'): string => {
  const ringgit = cents / 100;
  return `${currencyPrefix} ${ringgit.toFixed(2)}`;
};

/**
 * Parse currency string to cents
 * @param value - Currency string (e.g., "RM 12.50", "12.50", "12")
 * @returns Value in cents (integer)
 */
export const parseCurrency = (value: string): number => {
  const numericValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Math.round(numericValue * 100);
};

/**
 * Convert cents to ringgit (decimal)
 * @param cents - Value in cents
 * @returns Value in ringgit as number
 */
export const centsToRinggit = (cents: number): number => cents / 100;

/**
 * Convert ringgit to cents
 * @param ringgit - Value in ringgit
 * @returns Value in cents (rounded to integer)
 */
export const ringgitToCents = (ringgit: number): number => Math.round(ringgit * 100);

/**
 * Format cents value as ringgit number for input fields
 * @param cents - Value in cents
 * @returns Formatted ringgit value as string (e.g., "12.50")
 */
export const formatCentsToRinggit = (cents: number): string => {
  const ringgit = cents / 100;
  return ringgit.toFixed(2);
};

/**
 * Parse ringgit input string to cents
 * @param ringgitString - Ringgit value as string (e.g., "12.50")
 * @returns Value in cents (integer)
 */
export const formatRinggitToCents = (ringgitString: string): number => {
  const ringgit = parseFloat(ringgitString) || 0;
  return Math.round(ringgit * 100);
};